import { create } from "zustand";
import { NotificationMessageDto } from "@/modules/types/notification/notification";

interface NotificationStore {
  totalNotification: number;
  totalUnReadNotification: number;
  totalReadNotification: number;
  notifications: NotificationMessageDto[];
  unReadNotifications: NotificationMessageDto[];
  readNotifications: NotificationMessageDto[];
  nextId: string | undefined;
  nextUnReadId: string | undefined;
  nextReadId: string | undefined;
  addNotification: (notification: NotificationMessageDto) => void;
  addUnReadNotification: (notification: NotificationMessageDto) => void;
  addReadNotification: (notification: NotificationMessageDto) => void;
  setNotifications: (notifications: NotificationMessageDto[], nextId?: string) => void;
  setUnReadNotifications: (notifications: NotificationMessageDto[], nextId?: string) => void;
  setReadNotifications: (notifications: NotificationMessageDto[], nextId?: string) => void;
  appendNotifications: (notifications: NotificationMessageDto[], nextId?: string) => void;
  appendUnReadNotifications: (notifications: NotificationMessageDto[], nextId?: string) => void;
  appendReadNotifications: (notifications: NotificationMessageDto[], nextId?: string) => void;
  markAsSeen: (notificationId: string) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  clearUnReadNotifications: () => void;
  clearReadNotifications: () => void;
  removeUnReadNotification: (notificationId: string) => void;
  removeReadNotification: (notificationId: string) => void;
  setTotalNotification: (total: number) => void;
  setTotalUnReadNotification: (total: number) => void;
  setTotalReadNotification: (total: number) => void;
}

export const useNotificationStore = create<NotificationStore>((set) => ({
  totalNotification: 0,
  totalUnReadNotification: 0,
  totalReadNotification: 0,
  notifications: [],
  unReadNotifications: [],
  readNotifications: [],
  nextId: undefined,
  nextUnReadId: undefined,
  nextReadId: undefined,
  addNotification: (notification) =>
    set((state) => ({ notifications: [...state.notifications, notification] })),
  addUnReadNotification: (notification) =>
    set((state) => ({
      unReadNotifications: [...state.unReadNotifications, notification],
    })),
  addReadNotification: (notification) =>
    set((state) => ({
      readNotifications: [...state.readNotifications, notification],
    })),
  setNotifications: (notifications, nextId) => set({ notifications, nextId }),
  setUnReadNotifications: (notifications, nextUnReadId) =>
    set({ unReadNotifications: notifications, nextUnReadId }),
  setReadNotifications: (notifications, nextReadId) =>
    set({ readNotifications: notifications, nextReadId }),
  appendNotifications: (notifications, nextId) =>
    set((state) => ({
      notifications: [...state.notifications, ...notifications],
      nextId,
    })),
  appendUnReadNotifications: (notifications, nextUnReadId) =>
    set((state) => ({
      unReadNotifications: [...state.unReadNotifications, ...notifications],
      nextUnReadId,
    })),
  appendReadNotifications: (notifications, nextReadId) =>
    set((state) => ({
      readNotifications: [...state.readNotifications, ...notifications],
      nextReadId,
    })),
  markAsSeen: (notificationId) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === notificationId ? { ...n, isSeen: true } : n
      ),
      unReadNotifications: state.unReadNotifications.map((n) =>
        n.id === notificationId ? { ...n, isSeen: true } : n
      ),
    })),
  markAsRead: (notificationId) =>
    set((state) => {
      const notification = state.unReadNotifications.find(
        (n) => n.id === notificationId
      );
      return {
        notifications: state.notifications.map((n) =>
          n.id === notificationId ? { ...n, isRead: true } : n
        ),
        unReadNotifications: state.unReadNotifications.filter(
          (n) => n.id !== notificationId
        ),
        readNotifications: notification
          ? [...state.readNotifications, { ...notification, isRead: true }]
          : state.readNotifications,
      };
    }),
  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unReadNotifications: [],
      readNotifications: [
        ...state.readNotifications,
        ...state.unReadNotifications.map((n) => ({ ...n, isRead: true })),
      ],
    })),
  clearNotifications: () =>
    set({
      notifications: [],
      unReadNotifications: [],
      readNotifications: [],
      nextId: undefined,
      nextUnReadId: undefined,
      nextReadId: undefined,
    }),
  clearUnReadNotifications: () =>
    set({ unReadNotifications: [], nextUnReadId: undefined }),
  clearReadNotifications: () =>
    set({ readNotifications: [], nextReadId: undefined }),
  removeUnReadNotification: (notificationId: string) =>
    set((state) => ({
      unReadNotifications: state.unReadNotifications.filter(
        (n) => n.id !== notificationId
      ),
    })),
  removeReadNotification: (notificationId: string) =>
    set((state) => ({
      readNotifications: state.readNotifications.filter(
        (n) => n.id !== notificationId
      ),
    })),
  setTotalNotification: (total) => set({ totalNotification: total }),
  setTotalUnReadNotification: (total) =>
    set({ totalUnReadNotification: total }),
  setTotalReadNotification: (total) => set({ totalReadNotification: total }),
}));
